'use client';

import { UploadIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import type { DropEvent, DropzoneOptions, FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';

import { cn } from '../utils/cn';

type DropzoneContextType = {
  src?: File[];
  accept?: DropzoneOptions['accept'];
  maxSize?: DropzoneOptions['maxSize'];
  minSize?: DropzoneOptions['minSize'];
  maxFiles?: DropzoneOptions['maxFiles'];
  translations?: {
    clickToReplace?: string;
    dragAndDrop?: string;
    uploadFile?: string;
    uploadFiles?: string;
  };
};

const renderBytes = (bytes: number) => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(2)}${units[unitIndex]}`;
};

const DropzoneContext = createContext<DropzoneContextType | undefined>(
  undefined,
);

export type DropzoneProps = Omit<DropzoneOptions, 'onDrop'> & {
  src?: File[];
  className?: string;
  translations?: {
    clickToReplace?: string;
    dragAndDrop?: string;
    uploadFile?: string;
    uploadFiles?: string;
  };
  onDrop?: (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
    event: DropEvent,
  ) => void;
  children?: ReactNode;
};

export const Dropzone = ({
  accept,
  maxFiles = 1,
  maxSize,
  minSize,
  onDrop,
  onError,
  disabled,
  src,
  className,
  translations,
  children,
  ...props
}: DropzoneProps) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    maxFiles,
    maxSize,
    minSize,
    onError,
    disabled,
    onDrop: (acceptedFiles, fileRejections, event) => {
      if (fileRejections.length > 0) {
        const message = fileRejections.at(0)?.errors.at(0)?.message;
        onError?.(new Error(message));
        return;
      }

      onDrop?.(acceptedFiles, fileRejections, event);
    },
    ...props,
  });

  return (
    <DropzoneContext.Provider
      key={JSON.stringify(src)}
      value={{ src, accept, maxSize, minSize, maxFiles, translations }}
    >
      {/*
        Do not use ui Button here. Button ships whitespace-nowrap + inline-flex,
        which on iOS WKWebView lets long upload copy expand past the card.
        Pass className into getRootProps so dropzone does not clobber styles.
      */}
      <button
        type="button"
        disabled={disabled}
        {...getRootProps({
          className: cn(
            'relative box-border flex h-auto w-full min-w-0 max-w-full flex-col items-center justify-center gap-2 overflow-hidden whitespace-normal break-words rounded-md border border-input bg-card p-4 text-center text-sm text-accent-foreground shadow-xs transition-[color,box-shadow] dark:bg-input/30',
            'hover:bg-accent disabled:pointer-events-none disabled:opacity-60',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            isDragActive && 'ring-1 ring-ring',
            className,
          ),
        })}
      >
        {/*
          react-dropzone defaults the file input to display:block; width/height:0.
          On iOS WKWebView that still expands the scroll width. Use a clipped
          absolute 1×1 control instead (display:none breaks programmatic click).
        */}
        <input
          {...getInputProps({
            className: 'pointer-events-none',
            style: {
              position: 'absolute',
              left: 0,
              top: 0,
              width: '1px',
              height: '1px',
              maxWidth: '1px',
              maxHeight: '1px',
              margin: '-1px',
              padding: 0,
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              clipPath: 'inset(50%)',
              border: 0,
              opacity: 0,
              whiteSpace: 'nowrap',
            },
          })}
          disabled={disabled}
        />
        {children}
      </button>
    </DropzoneContext.Provider>
  );
};

const useDropzoneContext = () => {
  const context = useContext(DropzoneContext);

  if (!context) {
    throw new Error('useDropzoneContext must be used within a Dropzone');
  }

  return context;
};

export type DropzoneContentProps = {
  children?: ReactNode;
  className?: string;
};

export const DropzoneContent = ({
  children,
  className,
}: DropzoneContentProps) => {
  const { src, translations } = useDropzoneContext();

  if (!src) {
    return null;
  }

  if (children) {
    return children;
  }

  return (
    <div
      className={cn(
        'flex min-w-0 max-w-full flex-col items-center justify-center',
        className,
      )}
    >
      <div className="flex size-8 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <UploadIcon size={16} />
      </div>
      <p className="my-2 w-full min-w-0 max-w-full text-pretty break-words font-medium text-sm">
        {translations?.clickToReplace || 'Replace file'}
      </p>
    </div>
  );
};

export type DropzoneEmptyStateProps = {
  children?: ReactNode;
  className?: string;
};

export const DropzoneEmptyState = ({
  children,
  className,
}: DropzoneEmptyStateProps) => {
  const { src, accept, maxSize, minSize, maxFiles, translations } =
    useDropzoneContext();

  if (src) {
    return null;
  }

  if (children) {
    return (
      <div className={cn('min-w-0 max-w-full overflow-hidden', className)}>
        {children}
      </div>
    );
  }

  let caption = '';

  if (accept) {
    caption += 'Accepts ';
    caption += new Intl.ListFormat('en').format(Object.keys(accept));
  }

  if (minSize && maxSize) {
    caption += ` between ${renderBytes(minSize)} and ${renderBytes(maxSize)}`;
  } else if (minSize) {
    caption += ` at least ${renderBytes(minSize)}`;
  } else if (maxSize) {
    caption += ` less than ${renderBytes(maxSize)}`;
  }

  return (
    <div
      className={cn(
        'flex min-w-0 max-w-full flex-col items-center justify-center',
        className,
      )}
    >
      <div className="flex size-8 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <UploadIcon size={16} />
      </div>
      <p className="my-2 w-full min-w-0 max-w-full text-pretty break-words font-medium text-sm">
        {maxFiles === 1
          ? translations?.uploadFile || 'Upload a file'
          : translations?.uploadFiles || 'Upload files'}
      </p>
      <p className="w-full min-w-0 max-w-full text-pretty break-words text-muted-foreground text-xs">
        {translations?.dragAndDrop || 'Drag and drop or click to upload'}
      </p>
      {caption ? (
        <p className="w-full min-w-0 max-w-full text-pretty break-words text-muted-foreground text-xs">
          {caption}.
        </p>
      ) : null}
    </div>
  );
};

Dropzone.Content = DropzoneContent;
Dropzone.EmptyState = DropzoneEmptyState;
